import { createFileRoute } from '@tanstack/react-router';
import { createUserSchema } from '@tasks/contracts/users';
import { type FormEvent, useState } from 'react';
import { formValue } from '#lib/form-value';
import { ApiError } from '#lib/api-client';
import { useCreateUser, useMe, useUpdateUser, useUsers } from '#features/users/api';
import { Button } from '#components/ui/button';
import { useCanWrite } from '#lib/auth/vitto-session';
import { useHostMode } from '#lib/host/host-mode-context';

const NewUserForm = ({ onError }: { onError: (message: string | null) => void }) => {
  const create = useCreateUser();

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const parsed = createUserSchema.safeParse({
      email: formValue(data, 'email'),
      handle: formValue(data, 'handle'),
      name: formValue(data, 'name'),
      password: formValue(data, 'password'),
      role: formValue(data, 'role') || 'member',
    });
    if (!parsed.success) {
      onError("Check the new user's details.");
      return;
    }
    try {
      await create.mutateAsync(parsed.data);
      onError(null);
      form.reset();
    } catch (error) {
      onError(error instanceof ApiError ? error.message : 'Could not create the account.');
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="mt-6 grid gap-2 sm:grid-cols-5">
      {['name', 'handle', 'email', 'password'].map((field) => (
        <input
          key={field}
          name={field}
          type={field === 'password' ? 'password' : field === 'email' ? 'email' : 'text'}
          placeholder={field}
          required
          className="rounded-sm border border-input px-2 py-1.5 text-sm bg-background"
        />
      ))}
      <Button type="submit" size="sm" disabled={create.isPending}>
        Add
      </Button>
    </form>
  );
};

const UsersPage = () => {
  const users = useUsers();
  const me = useMe();
  const update = useUpdateUser();
  const [message, setMessage] = useState<string | null>(null);
  const canWrite = useCanWrite();
  // Inside a Vitto Space people come in through Vitto: this app never creates an account with a password there.
  const embedded = useHostMode().kind === 'embedded';
  const isAdmin = me.data?.role === 'admin' && canWrite;

  return (
    <main className="mx-auto max-w-3xl p-6">
      <h1 className="text-xl">People</h1>
      <ul className="mt-4 divide-y divide-border">
        {(users.data ?? []).map((user) => (
          <li key={user.id} className="flex items-center justify-between py-3 text-sm">
            <div>
              <p className="font-medium">
                {user.name} <span className="text-muted-foreground">@{user.handle}</span>
              </p>
              <p className="text-muted-foreground">
                {user.email} · {user.role}
                {user.isActive ? '' : ' · deactivated'}
              </p>
            </div>
            {isAdmin && user.id !== me.data?.id ? (
              <Button
                type="button"
                variant="link"
                size="inline"
                onClick={() => update.mutate({ id: user.id, patch: { isActive: !user.isActive } })}
              >
                {user.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {message === null ? null : (
        <p role="alert" className="mt-3 text-sm text-destructive-text">
          {message}
        </p>
      )}
      {isAdmin && !embedded ? <NewUserForm onError={setMessage} /> : null}
    </main>
  );
};

export const Route = createFileRoute('/_authed/settings/users')({ component: UsersPage });
